import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AnalyticEvents } from '@app/enums';
import { PlatformModule } from '@app/platform.module';
import { AnalyticService } from '@app/services';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

export type ChipItem<T = string> = {
    displayValue: string;
    value: T;
};

@Component({
    selector: 'app-chip-filters',
    templateUrl: './chip-filters-list.component.html',
    styleUrls: ['./chip-filters-list.component.scss'],
    standalone: true,
    imports: [PlatformModule, TranslateModule, CommonModule],
})
export class ChipFilterList {
    @Input() chips: ChipItem[] = [];
    @Input('aria-label') ariaLabel?: string;
    @Output() chipSelected = new EventEmitter<ChipItem>();
    selectedChipIndex: number | null = null;
    selectedChipValue: string | null = null;

    // The gap between each chip item in pixels
    readonly MENU_ITEM_GAP = 8;

    numItemsFitInScreenWidth?: number;

    // The horizontal width (in pixels) required to render the "more" button
    private moreButtonWidth = 0;

    private filteredChipElements: HTMLElement[] = [];
    private _onWindowResize$ = new Subject<number>();

    constructor(
        private analyticService: AnalyticService,
        private element: ElementRef<HTMLElement>
    ) {
        this._onWindowResize$.pipe(takeUntilDestroyed(), debounceTime(100)).subscribe((entireChipListWidth) => {
            this.calculateVisibleChips(entireChipListWidth);
        });
    }

    ngOnInit(): void {
        this.setDefaultSelection();
        setTimeout(() => {
            this.filteredChipElements = Array.from(
                this.element.nativeElement.querySelectorAll('mat-chip-option')
            ).filter((el): el is HTMLElement => el instanceof HTMLElement && el.scrollWidth > 0 && el.id !== 'more');

            const moreButton = this.element.nativeElement.querySelector('#more-button') as HTMLElement;
            if (moreButton) {
                this.moreButtonWidth = moreButton.scrollWidth;
            }
            this.validateHorizontalWidth();
        });
    }

    @HostListener('window:resize')
    validateHorizontalWidth(): void {
        this._onWindowResize$.next(this.element.nativeElement.offsetWidth);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.chips && this.chips.length > 0) {
            this.setDefaultSelection();
        }
    }

    setDefaultSelection(): void {
        if (this.chips.length > 0 && this.selectedChipIndex === null) {
            this.selectedChipIndex = 0;
            this.chipSelected.emit(this.chips[this.selectedChipIndex]);
        }
    }

    selectChip(index: number): void {
        if (index === this.selectedChipIndex) {
            return;
        }
        this.selectedChipIndex = index;
        this.chipSelected.emit(this.chips[index]);
    }

    isItemHidden(itemNumber: number): boolean {
        return this.numItemsFitInScreenWidth ? itemNumber >= this.numItemsFitInScreenWidth : false;
    }

    onMoreClick(): void {
        this.analyticService.trackEvent(AnalyticEvents.EventsViewMore);
    }

    private calculateVisibleChips(entireChipListWidth: number): void {
        let childrenWidthSum = this.moreButtonWidth;

        const allItemsFit = this.filteredChipElements.every((child, i) => {
            childrenWidthSum += child.offsetWidth + this.MENU_ITEM_GAP;

            // If we are checking the very last of the menu items, we no longer consider the "more" button width
            // This is so that we aren't hiding a single item, only to show "more" when that item could have fit on its own
            if (i === this.filteredChipElements.length - 1) {
                childrenWidthSum -= this.moreButtonWidth;
            }

            // If the sum has now become greater than the container it's in, we mark the number of items that should still be visible and return false to exit the loop early
            if (childrenWidthSum > entireChipListWidth) {
                this.numItemsFitInScreenWidth = i;
                return false;
            }

            // At this point, we've determined the sum is not yet greater than the container width, so we return true since the items so far will all fit
            return true;
        });

        if (allItemsFit) {
            // Signifies that we don't need to hide any items.
            this.numItemsFitInScreenWidth = undefined;
        }
    }
}
